import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck, AlertCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing reset token in the URL — request a fresh link from /forgot-password.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/password/verify-link`, { params: { token } });
        setValid(true);
        setEmail(r.data.email);
      } catch (err) {
        setError(err?.response?.data?.detail || "This reset link is no longer valid.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pwd !== pwd2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/password/reset-with-token`, { token, new_password: pwd });
      toast.success("Password updated — sign in with your new password");
      setTimeout(() => navigate("/", { replace: true }), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] paper-grain flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md p-8 border-neutral-200 shadow-sm" data-testid="reset-password-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Choose a new password</h1>
            {email && <p className="text-xs text-slate-500">For <span className="font-semibold text-slate-700">{email}</span></p>}
          </div>
        </div>

        {loading && <div className="space-y-3"><Skeleton className="h-12" /><Skeleton className="h-12" /><Skeleton className="h-12" /></div>}

        {!loading && error && (
          <>
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-4 text-sm text-rose-900 flex items-start gap-2" data-testid="reset-error">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{error}</span>
            </div>
            <Link to="/forgot-password" className="inline-block mt-4 text-sm font-semibold text-slate-900 underline" data-testid="request-new-link">
              Request a new reset link
            </Link>
          </>
        )}

        {!loading && valid && (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Link verified — set a fresh password below. This link can be used only once.</span>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">New password</label>
              <Input type="password" required minLength={8} autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="At least 8 characters" className="h-12 mt-1.5" data-testid="reset-pwd" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Confirm password</label>
              <Input type="password" required minLength={8} value={pwd2} onChange={(e) => setPwd2(e.target.value)} placeholder="Re-type your new password" className="h-12 mt-1.5" data-testid="reset-pwd2" />
            </div>
            <Button type="submit" disabled={busy || pwd.length < 8 || pwd !== pwd2} className="w-full h-12 bg-slate-900 hover:bg-slate-800" data-testid="reset-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save new password {!busy && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
